import { ANNUALLY } from '@constants/billingPeriods'
import { PLN } from '@constants/currencies'
import { formatNumericBasedOnLanguage } from '@helpers/data'
import { isAvailablePlan } from '@pages/Checkout/helpers/isAvailablePlan'
import { isUnavailablePlan } from '@pages/Checkout/helpers/isUnavailablePlan'
import { visits } from '@pages/Checkout/hooks/useGetPlanLimits'
import { useGetPurchaseSummaryInfo } from '@pages/Checkout/hooks/useGetPurchaseSummaryInfo'
import { SaveAmountOnBadge } from '@pages/Checkout/routes/components/SaveAmountOnBadge'
import { useCheckoutContext } from '@pages/Checkout/routes/contexts'
import { PAYMENTS } from '@routes/path'
import {
  Button,
  Divider,
  Heading,
  Icon,
  Panel,
  Paragraph,
  Spacer,
  Spreader
} from '@ui-kit'
import { Fragment } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Column, Row } from 'simple-flexbox'

import { AnnuallyPrice } from './components/AnnuallyPrice'
import { TotalPrice } from './components/TotalPrice'
import { VatAmount } from './components/VatAmount'
import { getMonthlyPrice } from './helpers/getMonthlyPrice'
import { getPromoBadge } from './helpers/getPromoBadge'
import styles from './PurchaseSummaryPanel.module.scss'

export const PurchaseSummaryPanel = () => {
  const { t } = useTranslation()

  const {
    goToStep,
    accountCurrency,
    chosenBillingPeriod,
    checkoutDataInContext: { chosenRecordKey }
  } = useCheckoutContext()

  const {
    planName,
    planPricePerMonth,
    planPriceInMonthlyPeriod,
    finalPrice,
    vatAmount,
    totalPrice,
    activePromotionKey,
    discount,
    planPricePerMonthWithoutDiscount
  } = useGetPurchaseSummaryInfo()

  const shouldRenderVisits = chosenRecordKey && isAvailablePlan(chosenRecordKey)

  const isNotSupportedPlan = chosenRecordKey
    ? isUnavailablePlan(chosenRecordKey)
    : false

  // TODO: dokończyć continue to payment
  const handleContinueToPayment = () => {
    goToStep(PAYMENTS.CHECKOUT.PAYMENT)
  }

  return (
    <Column>
      <Panel padding={30} surface={2} className={styles['summary-panel']}>
        <Heading level={2} weight={600}>
          {t('checkout.configure.purchase.summary.heading')}
        </Heading>

        <Spacer space={20} />

        <div className={styles['summary-panel__price']}>
          <Column>
            <Paragraph weight={600} size={16}>
              {t('checkout.configure.purchase.summary.plan.name', {
                planName
              })}
            </Paragraph>

            {isNotSupportedPlan && <Paragraph>({t('word.old')})</Paragraph>}

            {shouldRenderVisits && (
              <Paragraph color='neutral-6'>
                {t('checkout.configure.purchase.summary.visits', {
                  visits: formatNumericBasedOnLanguage(visits[chosenRecordKey])
                })}
              </Paragraph>
            )}
          </Column>

          {chosenBillingPeriod === ANNUALLY ? (
            <AnnuallyPrice
              planPricePerMonth={planPricePerMonth}
              planPriceInMonthlyPeriod={planPriceInMonthlyPeriod}
              planPricePerMonthWithoutDiscount={
                planPricePerMonthWithoutDiscount
              }
              discount={discount}
            />
          ) : (
            getMonthlyPrice({
              finalPrice,
              basicPrice: planPricePerMonth,
              planPricePerMonthWithoutDiscount,
              activePromotionKey,
              discount
            })
          )}
        </div>

        {getPromoBadge({
          activePromotionKey,
          discount,
          currency: accountCurrency
        })}

        {accountCurrency !== PLN && <Spacer space={15} />}

        {accountCurrency === PLN && <VatAmount vatAmount={vatAmount} />}

        <Divider />

        <Spacer space={15} />

        {chosenBillingPeriod === ANNUALLY && (
          <Fragment>
            <Row justifyContent='flex-end'>
              <SaveAmountOnBadge />
            </Row>

            <Spacer space={5} />
          </Fragment>
        )}

        <TotalPrice totalPrice={totalPrice} />

        <Spacer space={30} />

        <Button onClick={handleContinueToPayment} fullWidth>
          {t('checkout.configure.purchase.summary.continue')}
        </Button>
      </Panel>

      <Spacer space={30} />

      <Row>
        <Icon icon='icon-ok' />

        <Spreader spread={10} />

        <Paragraph size={12}>
          <Trans i18nKey='checkout.configure.purchase.summary.you.can.cancel' />
        </Paragraph>
      </Row>

      <Spacer space={15} />

      <Row>
        <Icon icon='icon-shield' color='success' />

        <Spreader spread={10} />

        <Paragraph size={12}>
          {t('checkout.configure.purchase.summary.money.back.guarantee')}
        </Paragraph>
      </Row>
    </Column>
  )
}
